import { useIntl } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconSearch } from '#src/atoms/icons/system/iconSearch.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { useIsInTraining } from '#src/hooks/commonGame/useIsInTraining.ts';
import { useEnableSorare27Nav } from '#src/hooks/useEnableSorare27Nav.ts';
import { navLabels } from '#src/lib/glossary.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import { HomeNavItem } from './HomeNavItem/index.tsx';
import { LineupsNavItem } from './LineupsNavItem/index.tsx';
import { MarketNavItem } from './MarketNavItem/index.tsx';
import { MoreNavDropdown } from './MoreNavDropdown/index.tsx';
import { MyCardsNavItem } from './MyCardsNavItem/index.tsx';
import { PlayNavItem } from './PlayNavItem/index.tsx';
import { ScoresNavItem } from './ScoresNavItem/index.tsx';
import { SearchNavItem } from './SearchNavItem/index.tsx';
import { useNavItemLayout } from './useNavItemLayout.ts';

const Root = styled(Horizontal).attrs({
  gap: 0,
})`
  width: 100%;
  position: relative;
  justify-content: space-between;

  & > * {
    flex: 1;
  }
  .seriesNav & {
    width: unset;
    background: var(--c-nl-500);
    backdrop-filter: blur(8px);
    border-radius: 64px;
    padding: var(--unit) var(--double-unit);
    justify-content: center;
    align-items: center;
    & > * {
      width: unset;
      min-width: calc(17 * var(--vw));
      padding: 0 var(--unit);
    }
  }
  @media ${laptopAndAbove} {
    flex-direction: column;
    align-items: stretch;
  }
`;

type Props = { sport: Sport; sidebarCollapsed?: boolean };
export const NavItems = ({ sport, sidebarCollapsed }: Props) => {
  const { formatMessage } = useIntl();
  const { isLaptopAndAbove, navItemsVariant } = useNavItemLayout();
  const isSorare27Nav = useEnableSorare27Nav();
  const isInTraining = useIsInTraining();
  const isPlayCentered = !isLaptopAndAbove && isSorare27Nav;

  if (isPlayCentered) {
    return (
      <Root>
        <MarketNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
        <ScoresNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
        <PlayNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
        <LineupsNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
        <MyCardsNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
      </Root>
    );
  }

  return (
    <Root>
      {!!isLaptopAndAbove && !!sidebarCollapsed && !isInTraining && (
        <SearchNavItem
          variant={navItemsVariant}
          icon={() => <FontAwesomeIcon icon={iconSearch} size="sm" />}
          label={formatMessage(navLabels.search)}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}
      <HomeNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
      <PlayNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
      <LineupsNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
      <ScoresNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
      <MarketNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
      <MyCardsNavItem sport={sport} sidebarCollapsed={sidebarCollapsed} />
      <MoreNavDropdown sidebarCollapsed={sidebarCollapsed} />
    </Root>
  );
};
